import type { EpocusEncounter } from "$global-providers/rxdb-provider/collections/epocus-encounter";
import { customAlphabet } from "nanoid";
import type { RxCollection } from "rxdb";

/**
 * Crockford base32: digits and uppercase letters, minus I, L, O and U. This
 * value is read off a probe screen and typed in by hand, so the transcription
 * pairs (I/L/1, O/0) are gone, and U so a random draw cannot spell an obscenity.
 *
 * Kept identical to `mintOrderAccession` in api-imaging-study: the server mints
 * the same shape when a client sends none, and an order should not be readable
 * one way or the other depending on which side minted it.
 */
const ACCESSION_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const ACCESSION_GROUP_LENGTH = 4;
const ACCESSION_GROUPS = 2;
const MAX_MINT_ATTEMPTS = 25;

const randomSymbols = customAlphabet(
  ACCESSION_ALPHABET,
  ACCESSION_GROUP_LENGTH * ACCESSION_GROUPS,
);

/**
 * `ABCD-EFGH` — nine characters, against the sixteen DICOM `AccessionNumber`
 * (0008,0050) allows. A device that truncates an over-long SH writes a
 * different accession into the study it produces, which then matches no order
 * and does so silently.
 */
export const randomOrderAccession = (): string => {
  const symbols = randomSymbols();
  const groups: string[] = [];
  for (let start = 0; start < symbols.length; start += ACCESSION_GROUP_LENGTH) {
    groups.push(symbols.slice(start, start + ACCESSION_GROUP_LENGTH));
  }
  return groups.join("-");
};

/**
 * The local replica is only the encounters this device can see, so this is a
 * courtesy rather than a guarantee — but it is worth making. The server matches
 * an unrecognised id by accession and *merges into* the encounter it finds
 * (`datasource.ts`), so a duplicate does not fail the push: it silently binds
 * the new order to an unrelated encounter. Drawing again is cheap; that is not.
 */
export const mintOrderAccession = async (
  encounterCollection: RxCollection<EpocusEncounter>,
): Promise<string> => {
  for (let attempt = 0; attempt < MAX_MINT_ATTEMPTS; attempt += 1) {
    const candidate = randomOrderAccession();
    const taken = await encounterCollection
      .findOne({ selector: { accessionNumber: candidate } })
      .exec();
    if (taken === null) return candidate;
  }

  throw new Error(
    `Could not mint a free accession in ${MAX_MINT_ATTEMPTS} attempts — this should be impossible and means the generator is not random`,
  );
};
